// packages/core/src/learning/SchoolExhibitMeta.ts
// ─────────────────────────────────────────────────────────────────────────
// The "destination" payload for each school. SchoolMeta answers
// "What is this school?". SchoolExhibitMeta answers "What world does it
// contain?".
//
// ── REFACTORED (drop-in authoring model) ─────────────────────────────────
// Exhibits are NO LONGER a single fat literal in this file. Each school's
// exhibit lives in its own file under ./schools/<school-id>.ts and exports
// one `SchoolExhibitEntry`. This file:
//   1. owns the TYPES (CanonItem, SchoolApplyAction, MarketingHook,
//      SchoolExhibitEntry) — unchanged, the single source of truth;
//   2. re-assembles SCHOOL_EXHIBITS from the ./schools registry;
//   3. keeps the SAME public exports (SCHOOL_EXHIBITS, getSchoolExhibit,
//      hasSchoolExhibit, listedExhibitIds) so consumers don't change;
//   4. runs a runtime validator (./schools/_validate) that console.warns on
//      issues the TYPE SYSTEM CANNOT catch (dangling lesson/debate ids,
//      empty required content, meta-without-exhibit). Warnings never block:
//      a broken or missing exhibit just falls back to the lean SchoolMeta view.
//
// To add a school: author ./schools/<id>.ts, then add its import + map entry
// in ./schools/index.ts. Same three-line ritual as lesson batches.
//
// Single source of truth rules (unchanged):
//   - This layer owns destination CONTENT only. It does not own lesson bodies.
//   - Lessons live in LearningModules; referenced here by id.
//   - Figures are referenced by FigureId from figureRegistry.ts.
//   - Debates live in DebateMeta.ts; referenced here by id.
// ─────────────────────────────────────────────────────────────────────────

import type { ListedSchool, ApplyAction } from './LearningTypes';
import type { FigureId } from './CurriculumMeta';

// ── Types (single source of truth — authored files import these) ──────────

/** A book, essay, framework, or famous idea taught in the school's tradition.
 *  Authored in Nisk's voice — never copy from the source. */
export interface CanonItem {
	/** The book title, essay name, or framework name. */
	title: string;
	/** Author or attribution. Omit for traditions ("the 4% rule"). */
	author?: string;
	/** One plainspoken sentence of the core idea. */
	oneLineIdea: string;
}

/** A pointer from a school to an in-app action — a calculator, simulator,
 *  dashboard, or written prompt — that lets the user test the school's ideas
 *  on their own numbers. */
export interface SchoolApplyAction {
	/** Display label, e.g. "See how aggressive saving changes your FI date". */
	label: string;
	/** The ApplyAction kind from the locked enum — reuses lesson tooling. */
	action: ApplyAction;
	/** Optional lesson id this action is most directly tied to. */
	lessonId?: string;
}

/** Short-form content hooks generated alongside curriculum content. Each
 *  hook is a single punchy sentence ready to become a TikTok line, an
 *  interview question, or a Kickstarter beat. */
export interface MarketingHook {
	/** The hook itself. */
	line: string;
	/** What kind of asset it suits best. */
	kind: 'hook' | 'interview-question' | 'kickstarter-beat' | 'creator-prompt';
}

export interface SchoolExhibitEntry {
	id: ListedSchool;

	// ── Identity (editorial header) ──────────────────────────────────────
	/** One-line hook in Nisk voice. Memorable, fair, slightly bold.
	 *  Pattern often works as "[School] believes…" or a punchy reframe. */
	tagline: string;
	/** A plainspoken paragraph (3–6 sentences) explaining how this school
	 *  sees money. Should give a beginner the worldview in one read. */
	worldviewSummary: string;

	// ── Beliefs ─────────────────────────────────────────────────────────
	/** 3–7 belief statements. Use "[School] believes…" framing for fairness. */
	coreBeliefs: string[];
	/** What this school argues against / warns about. 2–4 short statements. */
	warnsAgainst?: string[];
	/** Who this school helps most. 1–2 plain sentences. */
	helps?: string;
	/** The one thing this school structurally underweights or gets wrong — its
	 *  honest blind spot, in Nisk voice. 1 short sentence. Optional: rendered in
	 *  the Featured lens only when authored, never inferred from other fields. */
	blindSpot?: string;

	// ── People (referenced from figureRegistry) ─────────────────────────
	figures: FigureId[];

	// ── The canon (books, essays, ideas) ────────────────────────────────
	canon: CanonItem[];

	// ── Lessons (references — no lesson bodies live here) ───────────────
	/** Existing lessons (from anywhere) the school surfaces. Cross-references. */
	featuredLessonIds: string[];
	/** The school's own LearningModule id (surface: 'school'), if authored.
	 *  Inside it, lessons split by lessonKind: 'school-core' vs 'school-extra'. */
	nativeModuleId?: string;

	// ── Debates this school participates in (referenced) ────────────────
	debateIds: string[];

	// ── Apply it (action layer) ─────────────────────────────────────────
	applyActions: SchoolApplyAction[];

	// ── Marketing assets (same source as curriculum) ────────────────────
	marketingHooks: MarketingHook[];

	// ── Editorial revision marker for content versioning ────────────────
	revision: number;
}

// ── Assembly (from the ./schools registry) ────────────────────────────────
// SCHOOL_EXHIBITS is a Partial map: a school without an authored entry simply
// renders the lean SchoolMeta view. The registry collects every authored file.
import { SCHOOL_EXHIBITS } from './schools';
import { validateExhibits } from './schools/_validate';

export { SCHOOL_EXHIBITS };

// Run the runtime validator once, on first module load. console.warn only —
// never throws, never blocks render. Dev builds surface the warnings; prod
// builds typically strip console.warn via the bundler, so this is dev-facing.
validateExhibits(SCHOOL_EXHIBITS);

// ── Lookups (unchanged public surface) ────────────────────────────────────
export function getSchoolExhibit(id: string): SchoolExhibitEntry | undefined {
	return (SCHOOL_EXHIBITS as Record<string, SchoolExhibitEntry | undefined>)[
		id
	];
}

export function hasSchoolExhibit(id: string): boolean {
	return getSchoolExhibit(id) !== undefined;
}

